import { Play, Pause, Rewind, FastForward, Gauge, LocateFixed, RotateCcw, ParkingCircle, SlidersHorizontal, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { useRoutePlayback } from '../../hooks/useRoutePlayback';
import { formatApproxDistanceKm, type InterpolatedPosition } from '../../utils/vehicleHistory';

const SKIP_MS = 5000;

interface HistoryPlaybackPanelProps {
  playback: ReturnType<typeof useRoutePlayback>;
  position: InterpolatedPosition | null;
  distanceKm: number | null;
  /** El servidor adelgazó la polyline: la distancia sale corta y se avisa. */
  downsampled: boolean;
  stopsCount: number;
  showStops: boolean;
  vehicleLabel: string | null;
  onToggleStops: () => void;
  /** FE-27 — el mapa acompaña al vehículo (modo "Ubicar"). */
  isFollowing: boolean;
  onToggleFollow: () => void;
  onRestart: () => void;
  onEditQuery: () => void;
  /**
   * Cierra el histórico y deja el mapa limpio. Solo lo pasa la carcasa de
   * desktop: la página de mobile sale con la flecha atrás de su cabecera y el
   * formulario ya trae su "Limpiar".
   */
  onClose?: () => void;
}

/**
 * FE-27 — panel de reproducción FIJO, acoplado bajo el mapa. Es UNA sola pieza
 * para las dos carcasas: la página propia de mobile y el mapa de Monitoreo en
 * desktop (antes desktop tenía `RoutePlaybackBar`, una barra flotante con su
 * propio layout y su propio CSS; se retiró el 2026-09-20 a pedido del dueño).
 *
 * No flota sobre el mapa — así no compite con el selector de estilo, la
 * leyenda ni la `bottom-nav`, que fue lo que obligó a parchear la barra
 * flotante tres veces. En pantalla ancha (`≥ 1025px`, CSS) el mismo panel se
 * dispone en una sola fila para no quitarle tanto alto al mapa.
 */
export function HistoryPlaybackPanel({
  playback, position, distanceKm, downsampled, stopsCount, showStops, vehicleLabel,
  onToggleStops, isFollowing, onToggleFollow, onRestart, onEditQuery, onClose,
}: HistoryPlaybackPanelProps) {
  const { isPlaying, currentMs, startMs, endMs, speedLabel, play, pause, seek, cycleSpeed } = playback;
  const fmt = (ms: number, pattern: string) => format(new Date(ms), pattern, { locale: es });
  const currentAt = position ? Date.parse(position.occurredAt) : null;

  return (
    <div className="history-panel">
      <div className="history-panel__transport">
        <button className="history-panel__skip" onClick={() => seek(currentMs - SKIP_MS)} title="Retroceder 5 segundos">
          <Rewind size={14} />
        </button>
        <button className="history-panel__toggle" onClick={isPlaying ? pause : play} title={isPlaying ? 'Pausar' : 'Reproducir'}>
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button className="history-panel__skip" onClick={() => seek(currentMs + SKIP_MS)} title="Adelantar 5 segundos">
          <FastForward size={14} />
        </button>
        <input
          type="range"
          className="history-panel__slider"
          min={startMs}
          max={endMs}
          value={currentMs}
          onChange={e => seek(Number(e.target.value))}
          aria-label="Progreso del recorrido"
        />
        <button className="history-panel__speed" onClick={cycleSpeed} title="Velocidad de reproducción">
          <Gauge size={13} /> {speedLabel}
        </button>
      </div>

      <p className="history-panel__range">
        {fmt(startMs, 'dd/MM/yyyy HH:mm')} — {fmt(endMs, 'dd/MM/yyyy HH:mm')}
      </p>

      <div className="history-panel__stats">
        <div className="history-panel__stat">
          <span className="history-panel__stat-main">{currentAt !== null ? fmt(currentAt, 'HH:mm:ss') : '—'}</span>
          <span className="history-panel__stat-sub">{currentAt !== null ? fmt(currentAt, 'dd/MM/yyyy') : ' '}</span>
        </div>
        <div className="history-panel__stat">
          <span className="history-panel__stat-main">
            {position?.speedKmh !== undefined ? `${Math.round(position.speedKmh)} km/h` : '—'}
          </span>
          <span className="history-panel__stat-sub">velocidad</span>
        </div>
        <div
          className="history-panel__stat"
          title="Longitud aproximada del recorrido según los puntos GPS. No es el odómetro del equipo."
        >
          <span className="history-panel__stat-main">{distanceKm !== null ? `≈ ${formatApproxDistanceKm(distanceKm)}` : '—'}</span>
          <span className="history-panel__stat-sub">{downsampled ? 'aprox. por GPS · recortada' : 'aprox. por GPS'}</span>
        </div>
      </div>

      <div className="history-panel__actions">
        {vehicleLabel && <span className="history-panel__vehicle">{vehicleLabel}</span>}
        <button className="history-panel__action" onClick={onRestart} title="Reproducir desde el inicio">
          <RotateCcw size={15} /> <span>Reiniciar</span>
        </button>
        <button
          className={`history-panel__action${isFollowing ? ' history-panel__action--on' : ''}`}
          onClick={onToggleFollow}
          disabled={!position}
          aria-pressed={isFollowing}
          title={isFollowing ? 'Dejar de seguir al vehículo' : 'Ubicar el vehículo y seguirlo en el mapa'}
        >
          <LocateFixed size={15} /> <span>{isFollowing ? 'Siguiendo' : 'Ubicar'}</span>
        </button>
        <button
          className={`history-panel__action${showStops ? ' history-panel__action--on' : ''}`}
          onClick={onToggleStops}
          aria-pressed={showStops}
          title={`${showStops ? 'Ocultar' : 'Mostrar'} paradas${stopsCount > 0 ? ` (${stopsCount})` : ''}`}
        >
          <ParkingCircle size={15} /> <span>Paradas{stopsCount > 0 ? ` (${stopsCount})` : ''}</span>
        </button>
        <button className="history-panel__action" onClick={onEditQuery} title="Cambiar vehículo o rango">
          <SlidersHorizontal size={15} /> <span>Consulta</span>
        </button>
        {onClose && (
          <button className="history-panel__action history-panel__action--close" onClick={onClose} title="Cerrar el histórico">
            <X size={15} /> <span>Cerrar</span>
          </button>
        )}
      </div>
    </div>
  );
}
